"use client";

import { Button, Input, Label } from "@acme/ui";
import { cn } from "@acme/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import circlesLogo from "@public/static/circles-logo.png";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { signInWithEmail, signUpWithEmail } from "~/app/_actions/auth";
import { sanitizeReturnPath } from "~/features/auth/return-to";
import { signIn } from "~/lib/auth/better-auth-client";
import AuthRightPanel from "../right-pannel";
import TermsAndServicesAgreement from "../terms-and-services";

const emailSchema = z.string().email("Please enter a valid email address");
const passwordSchema = z
  .string()
  .min(6, "Password must be at least 6 characters");
const fullNameSchema = z
  .string()
  .min(2, "Full name must be at least 2 characters");

// Define validation schemas
const signInSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

const signUpSchema = z.object({
  fullName: fullNameSchema,
  email: emailSchema,
  password: passwordSchema,
  // .regex(
  //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/,
  //   "Password must contain at least one uppercase letter, one lowercase letter, and one number",
  // ),
});

type SignInFormValues = z.infer<typeof signInSchema>;
type SignUpFormValues = z.infer<typeof signUpSchema>;

/**
 * Hook to get and sanitize the return-to URL from query parameters.
 * Returns "/" as default if no valid return-to parameter is found.
 */
function useReturnToUrl(): string {
  const searchParams = useSearchParams();
  const returnTo = searchParams.get("return-to");
  return sanitizeReturnPath(returnTo, "/");
}

function SignInScreen() {
  const t = useTranslations("auth.login");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returnToUrl = useReturnToUrl();

  // Set up form validation with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    mode: "onBlur",
  });
  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        setLoading(true);
        setError(null);

        console.log("data", data);
        try {
          const signInData = data as SignInFormValues;
          const result = await signInWithEmail(
            signInData.email,
            signInData.password,
            returnToUrl,
          );
          if (result.isSuccess) {
            return;
          }
          setError(result.error.message || "Authentication error");
        } catch (error: any) {
          if (error.message === "NEXT_REDIRECT") {
            return;
          }

          console.error("Authentication error:", error);
          setError(
            error.message || "Invalid email or password. Please try again.",
          );
        } finally {
          setLoading(false);
        }
      })}
      className="grid w-full max-w-md gap-3 sm:gap-4"
    >
      <div className="grid gap-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="m@example.com"
          autoComplete="email"
          {...register("email")}
          aria-invalid={errors.email ? "true" : "false"}
        />
        {errors.email && (
          <p className="text-xs text-red-500">{errors.email.message}</p>
        )}
      </div>

      <div className="grid gap-2">
        <div className="flex items-center">
          <Label htmlFor="password" className="text-sm font-medium">
            {t("password")}
          </Label>

          <Link
            href="access/forgot"
            className="ml-auto text-sm font-medium text-primary hover:underline"
          >
            {t("forgotPassword")}
          </Link>
        </div>

        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("password")}
            autoComplete="current-password"
            {...register("password")}
            aria-invalid={errors.password ? "true" : "false"}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-300"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-500">{errors.password.message}</p>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <Button type="submit" className="mt-4 w-full" disabled={loading}>
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          t("loginBtn")
        )}
      </Button>
    </form>
  );
}

function SignUpScreen() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations("auth.login");
  const returnToUrl = useReturnToUrl();

  // Set up form validation with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    mode: "onBlur",
  });
  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        setLoading(true);
        setError(null);

        try {
          const signUpData = data as SignUpFormValues;
          const result = await signUpWithEmail(
            signUpData.fullName,
            signUpData.email,
            signUpData.password,
            returnToUrl,
          );
          if (!result.isSuccess) {
            setError(result.error.message || t("error.failedToCreateAccount"));
          }
        } catch (error: any) {
          if (error.message === "NEXT_REDIRECT") {
            return;
          }

          console.error("Authentication error:", error);
          setError(error.message || t("error.failedToCreateAccount"));
        } finally {
          setLoading(false);
        }
      })}
      className="grid w-full max-w-md gap-3 sm:gap-4"
    >
      <div className="grid gap-2">
        <Label htmlFor="fullName" className="text-sm font-medium">
          {t("fullName")}
        </Label>
        <Input
          id="fullName"
          type="text"
          placeholder={t("firstAndLastName")}
          autoComplete="name"
          {...register("fullName")}
          aria-invalid={errors.fullName ? "true" : "false"}
        />
        {errors.fullName && (
          <p className="text-xs text-red-500">{errors.fullName.message}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="m@example.com"
          autoComplete="email"
          {...register("email")}
          aria-invalid={errors.email ? "true" : "false"}
        />
        {errors.email && (
          <p className="text-xs text-red-500">{errors.email.message}</p>
        )}
      </div>

      <div className="grid gap-2">
        <div className="flex items-center">
          <Label htmlFor="password" className="text-sm font-medium">
            {t("password")}
          </Label>
        </div>

        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("password")}
            autoComplete="new-password"
            {...register("password")}
            aria-invalid={errors.password ? "true" : "false"}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-300"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-500">{errors.password.message}</p>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <Button type="submit" className="mt-4 w-full" disabled={loading}>
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          t("signUpBtn")
        )}
      </Button>
    </form>
  );
}

export default function AuthComponent() {
  const [isSignUp, setIsSignUp] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  const t = useTranslations("auth.login");
  const returnToUrl = useReturnToUrl();

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 px-2 py-4 sm:p-4 dark:from-gray-900 dark:to-gray-950">
      <div className="mx-auto my-2 flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-auto rounded-xl bg-white shadow-xl lg:flex-row dark:bg-gray-800">
        <div className="flex w-full flex-col items-center overflow-auto p-4 sm:p-6 md:p-12 lg:w-1/2 lg:items-start">
          <div className="mb-4 flex items-center sm:mb-6">
            <Image
              width={40}
              height={40}
              className="mr-2 h-8 w-auto"
              src={circlesLogo}
              alt="Logo"
            />
            <span className="text-lg font-semibold sm:text-xl">Optogrid</span>
          </div>

          <div className="mb-3 sm:mb-6">
            <h1 className="mb-1 text-xl font-bold tracking-tight sm:mb-2 sm:text-2xl">
              {isSignUp ? t("createYourAccount") : t("welcomeBack")}
            </h1>
            <p className="text-xs text-gray-500 sm:text-sm dark:text-gray-400">
              {isSignUp ? t("msgSignUp") : t("msgSignIn")}
            </p>
          </div>
          <div className="grid w-full max-w-md gap-3 sm:gap-4">
            <div className="flex w-full flex-col items-center justify-between gap-2">
              <GoogleSignInButton
                onClick={async () => {
                  await signIn.social({
                    provider: "google",
                    callbackURL: returnToUrl,
                  });
                }}
              >
                {isSignUp ? "Sign up with Google" : "Sign in with Google"}
              </GoogleSignInButton>
            </div>

            <div className="relative my-2 sm:my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {t("or")}
                </span>
              </div>
            </div>
          </div>
          {isSignUp ? <SignUpScreen /> : <SignInScreen />}
          <div className="mt-2 text-center text-sm text-gray-600 sm:mt-4 dark:text-gray-400">
            {isSignUp ? (
              <>
                {t("alreadyHaveAccount")}{" "}
                <Button
                  data-testid="switch-to-sign-in"
                  variant="link"
                  className="p-0 font-medium text-primary hover:underline"
                  onClick={() => {
                    setIsSignUp(false);
                    // setError(null);
                    // reset();
                  }}
                >
                  {t("signInBtn")}
                </Button>
              </>
            ) : (
              <>
                {t("dontHaveAccount")}{" "}
                <Button
                  data-testid="switch-to-sign-up"
                  variant="link"
                  className="p-0 font-medium text-primary hover:underline"
                  onClick={() => {
                    setIsSignUp(true);
                    // setError(null);
                    // reset();
                  }}
                >
                  {t("signUpBtn")}
                </Button>
              </>
            )}
          </div>

          <TermsAndServicesAgreement />
        </div>

        <AuthRightPanel />
      </div>
    </div>
  );
}

function GoogleSignInButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button variant="default" className={cn("w-full gap-2")} onClick={onClick}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="0.98em"
        height="1em"
        viewBox="0 0 256 262"
      >
        <path
          fill="#4285F4"
          d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
        ></path>
        <path
          fill="#34A853"
          d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
        ></path>
        <path
          fill="#FBBC05"
          d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"
        ></path>
        <path
          fill="#EB4335"
          d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
        ></path>
      </svg>
      {children}
    </Button>
  );
}
